import { Button } from '@ctw/shared/components/button';
import { Tooltip } from '@ctw/shared/components/tooltip';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faTrash, faTrashUndo } from '@fortawesome/pro-duotone-svg-icons';
import { faFileLines } from '@fortawesome/pro-regular-svg-icons';
// import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createContext, type PropsWithChildren, type ReactNode, useContext } from 'react';
import type { MergeExclusive } from 'type-fest';

export interface PatientFactGroup {
  label: string;
  facts: Array<PatientFact>;
}

export type PatientFactCitationUnavailableReason = 'loading' | 'error';

export interface PatientFactCitation {
  // Identifies which of the fact's citations this entry is, so a click opens
  // that specific source.
  citationId: number;
  // Whether the cited source can be opened (document viewer or resource drawer).
  // When false, the icon is shown but greyed out and not selectable.
  isOpenable: boolean;
  // Title of the cited resource. Shown inline in a multi-citation list and on
  // hover for a lone citation.
  label?: string;
  // Formatted date of the cited resource, shown after the label in a
  // multi-citation list. Omitted when the resource has no recognizable date.
  date?: string;
  // Why an unopenable citation can't be opened yet. Omitted when resolution
  // finished and found nothing to open.
  unavailableReason?: PatientFactCitationUnavailableReason;
}

const UNAVAILABLE_CITATION_TEXT: Record<PatientFactCitationUnavailableReason | 'none', string> = {
  loading: 'Loading source',
  error: 'Source could not be loaded',
  none: 'Source not available',
};

// Applied to a citation control that has nothing to open (still shown, greyed).
const CITATION_DISABLED_CLASS = 'pointer-events-none cursor-not-allowed opacity-40';

interface PatientFactNotFound {
  id: string;
  label: string;
}

interface PatientFactFound {
  id: string;
  label: string;
  value: ReactNode;
  // One entry per citation, in citation order. A single citation renders as a
  // hover-revealed icon; multiple render as a numbered list, revealed on row
  // hover/focus.
  citations?: Array<PatientFactCitation>;
}

export type PatientFact = MergeExclusive<PatientFactFound, PatientFactNotFound>;

const isFoundFact = (fact: PatientFact): fact is PatientFactFound => Object.hasOwn(fact, 'value');
const isNotFoundFact = (fact: PatientFact): fact is PatientFactNotFound =>
  !Object.hasOwn(fact, 'value');

export interface PatientFactCallbackArgs {
  fact: PatientFact;
}

export interface PatientFactCitationCallbackArgs {
  fact: PatientFact;
  citationId: number;
}

interface PatientFactExtractionGroupedTableState {
  isRedacted?: (fact: PatientFact) => boolean;
  onRedactFact?: (args: PatientFactCallbackArgs) => void;
  onOpenCitation?: (args: PatientFactCitationCallbackArgs) => void;
  showRemoveAction?: boolean;
}

const PatientFactExtractionGroupedTableContext = createContext<
  PatientFactExtractionGroupedTableState | undefined
>(undefined);

interface PatientFactExtractionGroupedTableProviderProps
  extends PropsWithChildren,
    PatientFactExtractionGroupedTableState {}

const PatientFactExtractionGroupedTableProvider = ({
  children,
  ...tableState
}: PatientFactExtractionGroupedTableProviderProps) => (
  <PatientFactExtractionGroupedTableContext.Provider value={tableState}>
    {children}
  </PatientFactExtractionGroupedTableContext.Provider>
);

const usePatientFactExtractionGroupedTableState = () => {
  const context = useContext(PatientFactExtractionGroupedTableContext);

  if (!context) {
    throw new Error(
      'usePatientFactExtractionGroupedTableState must be used within a PatientFactExtractionGroupedTable',
    );
  }

  return context;
};

export interface PatientFactExtractionGroupedTableProps
  extends PatientFactExtractionGroupedTableState {
  factGroups: Array<PatientFactGroup>;
}

export const PatientFactExtractionGroupedTable = ({
  factGroups,
  ...tableState
}: PatientFactExtractionGroupedTableProps) => (
  <PatientFactExtractionGroupedTableProvider {...tableState}>
    <div
      className={tw`divide-y divide-divider-main overflow-hidden rounded-md border border-border-main/80`}
    >
      {factGroups.map((factGroup) => (
        <PatientFactExtractionTableGroup key={factGroup.label} factGroup={factGroup} />
      ))}
    </div>
  </PatientFactExtractionGroupedTableProvider>
);

interface PatientFactExtractionTableGroupProps {
  factGroup: PatientFactGroup;
}

const PatientFactExtractionTableGroup = ({ factGroup }: PatientFactExtractionTableGroupProps) => (
  <div>
    <div
      className={tw`border-divider-main border-b bg-content-subtle/10 px-4 py-2 font-bold text-content-main/65 text-xs uppercase tracking-wide`}
    >
      {factGroup.label}
    </div>
    <div className={tw`divide-y divide-divider-main`}>
      {factGroup.facts.map((fact) => (
        <PatientFactExtractionTableItem key={fact.id} fact={fact} />
      ))}
    </div>
  </div>
);

interface PatientFactExtractionTableItemProps {
  fact: PatientFact;
}

const PatientFactExtractionTableItem = ({ fact }: PatientFactExtractionTableItemProps) => {
  const {
    isRedacted,
    onRedactFact,
    onOpenCitation,
    showRemoveAction = false,
  } = usePatientFactExtractionGroupedTableState();
  const redacted = isRedacted?.(fact) ?? false;
  const citations = (isFoundFact(fact) ? fact.citations : undefined) ?? [];
  const isMultiCitation = citations.length > 1;

  const handleToggleRedaction = () => {
    onRedactFact?.({ fact });
  };

  const citationTextFor = (citation: PatientFactCitation) =>
    citation.isOpenable
      ? 'View source'
      : UNAVAILABLE_CITATION_TEXT[citation.unavailableReason ?? 'none'];

  const openHandlerFor = (citation: PatientFactCitation) =>
    citation.isOpenable
      ? () => onOpenCitation?.({ fact, citationId: citation.citationId })
      : undefined;

  // A lone citation is a hover-revealed icon, its name in a tooltip. Multiple
  // render as a numbered list with name and date inline; each line's full name
  // is in a tooltip too, since it may be truncated.
  const singleCitation = citations.length === 1 ? citations[0] : undefined;
  const singleCitationText = singleCitation ? citationTextFor(singleCitation) : '';

  const singleCitationIcon = singleCitation ? (
    <Tooltip
      id={`fact-citation-${fact.id}`}
      place="left"
      content={
        singleCitation.isOpenable
          ? (singleCitation.label ?? singleCitationText)
          : singleCitationText
      }
    >
      <Button
        className={twx(
          'group/citation-button flex aspect-square items-center justify-center rounded-md p-1 transition-all duration-150',
          {
            'active:!scale-[.99] drop-shadow-sm hover:bg-info-main/10': singleCitation.isOpenable,
            [CITATION_DISABLED_CLASS]: !singleCitation.isOpenable,
          },
        )}
        type="button"
        variant="unstyled"
        disabled={!singleCitation.isOpenable}
        onClick={openHandlerFor(singleCitation)}
        ariaLabel={singleCitationText}
      >
        <FontAwesomeIcon
          icon={faFileLines}
          className={twx('h-3.5 w-3.5 text-content-icon', {
            'group-hover/citation-button:text-info-text': singleCitation.isOpenable,
          })}
        />
      </Button>
    </Tooltip>
  ) : null;

  const citationLines = isMultiCitation ? (
    <div className={tw`flex w-full min-w-0 flex-col gap-0.5`}>
      {citations.map((citation, index) => {
        const text = citationTextFor(citation);
        const line = (
          <Button
            key={citation.citationId}
            className={twx(
              // ml-auto right-aligns each content-width line; w-fit + max-w-full
              // lets it hug its content but never exceed the column, so the name
              // truncates instead of the line overflowing.
              'group/citation-line ml-auto flex w-fit min-w-0 max-w-full items-center gap-1.5 rounded-md px-1 py-0.5 transition-colors duration-150',
              {
                'hover:bg-info-main/10': citation.isOpenable,
                [CITATION_DISABLED_CLASS]: !citation.isOpenable,
              },
            )}
            type="button"
            variant="unstyled"
            disabled={!citation.isOpenable}
            onClick={openHandlerFor(citation)}
            // Each openable line is otherwise announced identically ("View
            // source"); the number and title make the accessible name distinct.
            ariaLabel={`${text}: citation ${index + 1}${citation.label ? `, ${citation.label}` : ''}`}
          >
            <span
              className={tw`min-w-4 shrink-0 whitespace-nowrap text-right font-medium text-content-icon text-xs tabular-nums`}
            >
              {index + 1}
            </span>
            <FontAwesomeIcon
              icon={faFileLines}
              className={twx('h-3.5 w-3.5 shrink-0 text-content-icon', {
                'group-hover/citation-line:text-info-text': citation.isOpenable,
              })}
            />
            {citation.isOpenable ? (
              <>
                {/* The name truncates first so the date, the more useful anchor, always stays. */}
                <span className={tw`min-w-0 truncate text-content-subtle text-xs`}>
                  {citation.label ?? text}
                </span>
                {citation.date && (
                  <span className={tw`shrink-0 whitespace-nowrap text-content-subtle text-xs`}>
                    {`• ${citation.date}`}
                  </span>
                )}
              </>
            ) : (
              <span className={tw`min-w-0 truncate text-content-subtle text-xs`}>{text}</span>
            )}
          </Button>
        );
        // The name is truncated, so a tooltip carries the full title for lines
        // that have one.
        return citation.isOpenable && citation.label ? (
          <Tooltip
            key={citation.citationId}
            className={tw`w-full min-w-0`}
            id={`fact-citation-${fact.id}-${citation.citationId}`}
            place="left"
            content={citation.label}
          >
            {line}
          </Tooltip>
        ) : (
          line
        );
      })}
    </div>
  ) : null;

  const removeButton = showRemoveAction ? (
    <Button
      className={twx(
        'group/delete-button active:!scale-[.90] flex aspect-square items-center justify-center rounded-md p-1 drop-shadow-sm transition-all duration-150 hover:shadow-sm',
        {
          'hover:bg-error-main/10': !redacted,
          'hover:bg-success-main/10': redacted,
        },
      )}
      type="button"
      variant="unstyled"
      onClick={handleToggleRedaction}
      ariaLabel={redacted ? 'Restore fact' : 'Remove fact'}
    >
      <FontAwesomeIcon
        icon={redacted ? faTrashUndo : faTrash}
        className={twx('h-3.5 w-3.5 text-content-icon', {
          'group-hover/delete-button:text-error-text': !redacted,
          'group-hover/delete-button:text-success-text': redacted,
        })}
      />
    </Button>
  ) : null;

  return (
    <div
      className={twx(
        'group/fact-row flex min-h-[3rem] items-center justify-between gap-4 px-4 py-2.5 hover:bg-content-disabled/5',
        {
          'bg-caution-background/70': !redacted && isNotFoundFact(fact),
          'bg-error-background/30': redacted,
        },
      )}
    >
      <div className={tw`flex min-w-0 flex-1 items-center gap-4`}>
        <div
          className={tw`flex shrink-0 basis-48 items-center gap-0.5 font-medium text-content-subtle text-sm`}
        >
          <div
            className={twx('break-words font-medium text-content-subtle text-sm', {
              'line-through': redacted,
            })}
          >
            {fact.label}
          </div>
        </div>
        <div className={tw`min-w-0 flex-1 break-words font-medium text-sm`}>
          {isFoundFact(fact) ? (
            <span
              // Multi-entry values (infection roll-ups, lab histories) arrive
              // newline-delimited from the extraction API; render each on its own line.
              className={twx('whitespace-pre-line', {
                'text-content-main': !redacted,
                'text-content-subtle line-through': redacted,
              })}
            >
              {fact.value}
            </span>
          ) : (
            <span
              className={twx('text-content-subtle', {
                'line-through': redacted,
              })}
            >
              <span aria-hidden="true">&mdash;</span>
              <span className={tw`sr-only`}>Not found</span>
            </span>
          )}
        </div>
      </div>
      {/* Collapsed to zero width until hover/focus, so the value fills the row
          and reclaims the space. The width snaps (not transitioned) so a long
          value re-wraps once, not per frame; the citations fade and grow in.
          Capped at min(14rem, 30%) so they never squeeze the value; names
          truncate. */}
      <div
        className={tw`grid max-w-[min(14rem,30%)] grid-cols-[0fr] group-hover/fact-row:grid-cols-[1fr] has-[:focus-visible]:grid-cols-[1fr]`}
      >
        <div
          className={tw`flex min-w-0 items-center justify-end gap-1 overflow-hidden opacity-0 transition-opacity duration-150 group-hover/fact-row:opacity-100 has-[:focus-visible]:opacity-100`}
        >
          {isMultiCitation ? (
            // grid-cols-1 gives the list the column's resolved width so long
            // names truncate; the 0fr→1fr row grows the height as it appears.
            <div
              className={tw`grid min-w-0 flex-1 grid-cols-1 grid-rows-[0fr] transition-[grid-template-rows] duration-200 group-hover/fact-row:grid-rows-[1fr] has-[:focus-visible]:grid-rows-[1fr]`}
            >
              <div className={tw`min-w-0 overflow-hidden`}>{citationLines}</div>
            </div>
          ) : (
            singleCitationIcon
          )}
          {removeButton}
        </div>
      </div>
    </div>
  );
};
